import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import {
  Assessment,
  Download,
  Refresh,
  FilterList,
  PictureAsPdf,
  TableChart
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchAuditorReport, fetchAuditorReportSummary } from '../redux/actions/reportActions';
import * as customerActions from '../redux/actions/customerActions';
import * as productActions from '../redux/actions/productActions';
import api from '../services/api';

const AuditorReport = () => {
  const dispatch = useDispatch();
  const { auditorReport, auditorReportSummary, loading, summaryLoading, error } = useSelector((state) => state.reports);
  const { customers } = useSelector((state) => state.customers);
  const { products } = useSelector((state) => state.products);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    category: '',
    customerId: ''
  });

  // Sorting and pagination states
  const [sortBy, setSortBy] = useState('invNo');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Category options
  const categoryOptions = [
    { value: 'GOLD', label: 'Gold' },
    { value: 'SILVER', label: 'Silver' },
    { value: 'DIAMOND', label: 'Diamond' },
    { value: 'GENERAL', label: 'General' }
  ];

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    // Load initial data
    dispatch(customerActions.getCustomers());
    dispatch(productActions.getProducts());
    loadReportSummary();
  }, [dispatch]);

  const loadReportSummary = () => {
    const summaryFilters = {};
    if (filters.startDate) {
      summaryFilters.startDate = formatDateForAPI(filters.startDate);
    }
    if (filters.endDate) {
      summaryFilters.endDate = formatDateForAPI(filters.endDate);
    }
    
    dispatch(fetchAuditorReportSummary(summaryFilters));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateReport = () => {
    const reportFilters = {};
    if (filters.startDate) {
      reportFilters.startDate = formatDateForAPI(filters.startDate);
    }
    if (filters.endDate) {
      reportFilters.endDate = formatDateForAPI(filters.endDate);
    }
    if (filters.category) {
      reportFilters.category = filters.category;
    }
    if (filters.customerId) {
      reportFilters.customerId = filters.customerId;
    }
    
    console.log('Generating report with filters:', reportFilters);
    dispatch(fetchAuditorReport(reportFilters));
    setPage(0); // Reset to first page when generating new report
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      category: '',
      customerId: ''
    });
  };

  const handleExportToExcel = () => {
    if (!auditorReport.reportData || auditorReport.reportData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare CSV data
    const headers = [
      'S.No',
      'Invoice No',
      'Invoice Date',
      'Customer Name',
      'Category',
      'Description',
      'Net Weight (g)',
      'Amount',
      'Tax Amount',
      'Total Amount'
    ];

    const csvData = [
      headers,
      ...auditorReport.reportData.map(row => [
        row.sNo,
        row.invNo,
        formatDate(row.invDate),
        row.customerName,
        row.category,
        row.description,
        row.netWeight,
        Math.round(parseFloat(row.amount)),
        Math.round(parseFloat(row.taxAmt)),
        Math.round(parseFloat(row.totalAmt))
      ]),
      [], // Empty row
      ['TOTALS', '', '', '', '', '', auditorReport.totals.totalWeight, Math.round(parseFloat(auditorReport.totals.totalAmount)), Math.round(parseFloat(auditorReport.totals.totalTax)), Math.round(parseFloat(auditorReport.totals.grandTotal))]
    ];

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `auditor_report_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportToPDF = async () => {
    if (!auditorReport.reportData || auditorReport.reportData.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      // Build query parameters from filters
      const params = new URLSearchParams();
      if (filters.startDate) {
        params.append('startDate', formatDateForAPI(filters.startDate));
      }
      if (filters.endDate) {
        params.append('endDate', formatDateForAPI(filters.endDate));
      }
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.customerId) {
        params.append('customerId', filters.customerId);
      }

      const queryString = params.toString();
      const url = queryString 
        ? `/reports/auditor/pdf?${queryString}` 
        : '/reports/auditor/pdf';

      // Use the API service to download the PDF
      const response = await api.get(url, {
        responseType: 'blob' // Important for binary data
      });

      // Get the PDF blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `auditor_report_${timestamp}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort and paginate data
  const sortedAndPaginatedData = useMemo(() => {
    if (!auditorReport.reportData || auditorReport.reportData.length === 0) {
      return [];
    }

    // Create a copy of the data to sort
    let sortedData = [...auditorReport.reportData];

    // Sort data
    sortedData.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle different data types
      if (sortBy === 'invNo' || sortBy === 'customerName' || sortBy === 'category' || sortBy === 'description') {
        // String comparison
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      } else if (sortBy === 'invDate') {
        // Date comparison
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      } else {
        // Numeric comparison
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }

      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    // Paginate data
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [auditorReport.reportData, sortBy, sortOrder, page, rowsPerPage]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Assessment sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Auditor Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive invoice details for auditing purposes
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterList sx={{ mr: 1 }} />
              <Typography variant="h6">Filters</Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">
                      <em>All Categories</em>
                    </MenuItem>
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={filters.customerId}
                    onChange={(e) => handleFilterChange('customerId', e.target.value)}
                    label="Customer"
                  >
                    <MenuItem value="">
                      <em>All Customers</em>
                    </MenuItem>
                    {customers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
              >
                Generate Report
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
              <Button
                variant="outlined"
                onClick={loadReportSummary}
                disabled={summaryLoading}
                startIcon={summaryLoading ? <CircularProgress size={20} /> : <Refresh />}
              >
                Refresh Summary
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {auditorReportSummary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Invoices
                  </Typography>
                  <Typography variant="h5">
                    {auditorReportSummary.summary.totalInvoices}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Amount
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {formatCurrency(auditorReportSummary.summary.totalAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Tax
                  </Typography>
                  <Typography variant="h5" color="secondary">
                    {formatCurrency(auditorReportSummary.summary.totalTax)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Date Range
                  </Typography>
                  <Typography variant="body2">
                    {auditorReportSummary.summary.dateRange}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Report Data */}
        {auditorReport.reportData.length > 0 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Report Data ({auditorReport.reportData.length} items)
                  {sortedAndPaginatedData.length > 0 && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      (Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, auditorReport.reportData.length)})
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<TableChart />}
                    onClick={handleExportToExcel}
                    color="primary"
                    size="small"
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    onClick={handleExportToPDF}
                    color="secondary"
                    size="small"
                  >
                    Export PDF
                  </Button>
                </Box>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>S.No</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'invNo'}
                          direction={sortBy === 'invNo' ? sortOrder : 'asc'}
                          onClick={() => handleSort('invNo')}
                        >
                          Invoice No
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'invDate'}
                          direction={sortBy === 'invDate' ? sortOrder : 'asc'}
                          onClick={() => handleSort('invDate')}
                        >
                          Invoice Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'customerName'}
                          direction={sortBy === 'customerName' ? sortOrder : 'asc'}
                          onClick={() => handleSort('customerName')}
                        >
                          Customer Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'category'}
                          direction={sortBy === 'category' ? sortOrder : 'asc'}
                          onClick={() => handleSort('category')}
                        >
                          Category
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'description'}
                          direction={sortBy === 'description' ? sortOrder : 'asc'}
                          onClick={() => handleSort('description')}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortBy === 'netWeight'}
                          direction={sortBy === 'netWeight' ? sortOrder : 'asc'}
                          onClick={() => handleSort('netWeight')}
                        >
                          Net Weight (g)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortBy === 'amount'}
                          direction={sortBy === 'amount' ? sortOrder : 'asc'}
                          onClick={() => handleSort('amount')}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortBy === 'taxAmt'}
                          direction={sortBy === 'taxAmt' ? sortOrder : 'asc'}
                          onClick={() => handleSort('taxAmt')}
                        >
                          Tax Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortBy === 'totalAmt'}
                          direction={sortBy === 'totalAmt' ? sortOrder : 'asc'}
                          onClick={() => handleSort('totalAmt')}
                        >
                          Total Amount
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAndPaginatedData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Chip label={row.invNo} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{formatDate(row.invDate)}</TableCell>
                        <TableCell>{row.customerName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.category} 
                            size="small" 
                            color={row.category === 'GOLD' ? 'warning' : row.category === 'SILVER' ? 'default' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell align="right">{typeof row.netWeight === 'number' ? row.netWeight.toFixed(3) : row.netWeight}</TableCell>
                        <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.taxAmt)}</TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(row.totalAmt)}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={auditorReport.reportData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
              />

              {/* Totals */}
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Total Weight
                    </Typography>
                    <Typography variant="h6">
                      {auditorReport.totals.totalWeight} g
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(auditorReport.totals.totalAmount)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Total Tax
                    </Typography>
                    <Typography variant="h6" color="secondary">
                      {formatCurrency(auditorReport.totals.totalTax)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Grand Total
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(auditorReport.totals.grandTotal)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* No Data Message */}
        {!loading && auditorReport.reportData.length === 0 && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Data Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generate a report using the filters above to view auditor data.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AuditorReport;
